import { Fragment, useEffect, useState } from "react";
import api, { apiErr, fmt2 } from "@/lib/api";
import { fl } from "@/lib/finI18n";
import { DrillNum } from "@/components/reports/Drill";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, XCircle, Save, ListTree } from "lucide-react";
import { toast } from "sonner";

const n = (v) => fmt2(v || 0);
const F = ["non_obj", "final", "pos", "neg"];
const PL_HEAD = ["Kode Akun", "Nama Akun", "Nilai Komersial", "Tidak Termasuk Objek Pajak", "Dikenakan PPh Final", "Objek Pajak Tidak Final",
  "Penyesuaian Fiskal Positif", "Penyesuaian Fiskal Negatif", "Kode Penyesuaian", "Nilai Fiskal"];
const ids = (ln) => (ln.accounts || []).map((a) => a.account_id).filter(Boolean).join(",");
const basisOf = (ln, part) => (part === "bs" || ln.src === "io" || ln.src === "ic" ? "cumulative" : "period");

function fiscal(code, k, a) {
  const obj = k - (+a.non_obj || 0) - (+a.final || 0);
  return { obj, fis: code[0] === "4" ? obj + (+a.pos || 0) - (+a.neg || 0) : obj - (+a.pos || 0) + (+a.neg || 0) };
}

function AccRows({ ln, part, cols }) {
  return (ln.accounts || []).map((a, i) => (
    <TableRow key={`${ln.code}-${i}`} className="text-muted-foreground">
      <TableCell />
      <TableCell className="text-xs italic pl-6">{a.account_code} {a.account_name}</TableCell>
      <TableCell className="text-right text-xs">{a.account_id ? <DrillNum value={a.amount} q={{ account: a.account_id, basis: basisOf(ln, part) }} title={`${a.account_code} ${a.account_name}`} testid={`ct-acc-${part}-${ln.code}-${i}`} /> : <span className="num">{n(a.amount)}</span>}</TableCell>
      {cols > 3 && <TableCell colSpan={cols - 3} />}
    </TableRow>
  ));
}

function PLLine({ ln, adj, setAdj, canEdit, showAcc }) {
  if (ln.kind === "h") return <TableRow className="bg-secondary/60"><TableCell /><TableCell colSpan={9} className="text-xs font-semibold uppercase tracking-wider text-primary">{ln.name}</TableCell></TableRow>;
  const tot = ln.kind === "t";
  const a = adj[ln.code] || {};
  const live = tot ? { obj: ln.obj, fis: ln.fiscal } : fiscal(ln.code, ln.commercial, a);
  const inp = (f, w = "w-24") => (
    <Input value={a[f] ?? ""} onChange={(e) => setAdj({ ...adj, [ln.code]: { ...a, [f]: e.target.value } })} inputMode="decimal"
      className={`h-7 ${w} text-right text-xs num ml-auto`} data-testid={`ct-adj-${ln.code}-${f}`} />
  );
  return (
    <Fragment>
      <TableRow className={tot ? (ln.code === "4800" ? "bg-primary/10 font-bold" : "bg-secondary/40 font-semibold") : ""} data-testid={`ct-pl-${ln.code}`}>
        <TableCell className="num text-xs">{ln.code}</TableCell>
        <TableCell className="text-xs">{ln.name}</TableCell>
        <TableCell className="text-right text-xs">{!tot && ids(ln) ? <DrillNum value={ln.commercial} q={{ account: ids(ln), basis: basisOf(ln, "pl") }} title={`${ln.code} ${ln.name}`} testid={`ct-drill-${ln.code}`} /> : <span className="num">{n(ln.commercial)}</span>}</TableCell>
        {F.slice(0, 2).map((f) => <TableCell key={f} className="text-right text-xs">{!tot && canEdit ? inp(f) : <span className="num">{n(tot ? ln[f] : a[f])}</span>}</TableCell>)}
        <TableCell className="num text-right text-xs">{n(live.obj)}</TableCell>
        {F.slice(2).map((f) => <TableCell key={f} className="text-right text-xs">{!tot && canEdit ? inp(f) : <span className="num">{n(tot ? ln[f] : a[f])}</span>}</TableCell>)}
        <TableCell className="text-xs">{!tot && canEdit ? <Input value={a.fcode ?? ""} onChange={(e) => setAdj({ ...adj, [ln.code]: { ...a, fcode: e.target.value } })} className="h-7 w-20 text-xs" data-testid={`ct-adj-${ln.code}-fcode`} /> : (a.fcode || "")}</TableCell>
        <TableCell className="num text-right text-xs font-semibold" data-testid={`ct-fiscal-${ln.code}`}>{n(live.fis)}</TableCell>
      </TableRow>
      {showAcc && !tot && <AccRows ln={ln} part="pl" cols={10} />}
    </Fragment>
  );
}

function BSCard({ report, mode, lang }) {
  return (
    <Card className="p-4 overflow-auto" data-testid="ct-bs">
      <h3 className="font-head font-semibold mb-2">{fl("ctBs", lang)}</h3>
      <Table>
        <TableHeader><TableRow><TableHead className="w-20">Kode Akun</TableHead><TableHead>Nama Akun</TableHead><TableHead className="text-right">Nilai</TableHead></TableRow></TableHeader>
        <TableBody>
          {report.bs.map((ln, i) => ln.kind === "h" ? (
            <TableRow key={i} className="bg-secondary/60"><TableCell /><TableCell colSpan={2} className="text-xs font-semibold uppercase tracking-wider text-primary">{ln.name}</TableCell></TableRow>
          ) : (
            <Fragment key={i}>
              <TableRow className={ln.kind === "t" ? "bg-secondary/40 font-semibold" : ""} data-testid={`ct-bs-${ln.code}`}>
                <TableCell className="num text-xs">{ln.code}</TableCell><TableCell className="text-xs">{ln.name}</TableCell>
                <TableCell className="text-right text-xs">{ln.kind === "a" && ids(ln) ? <DrillNum value={ln.value} q={{ account: ids(ln), basis: "cumulative" }} title={`${ln.code} ${ln.name}`} testid={`ct-drill-bs-${ln.code}`} /> : <span className="num">{n(ln.value)}</span>}</TableCell>
              </TableRow>
              {mode !== "summary" && ln.kind === "a" && <AccRows ln={ln} part="bs" cols={3} />}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function Mapping({ report, clientId, lang, canEdit, onReload }) {
  const save = async (id, code) => {
    try {
      await api.put("/coretax/settings", { client_id: clientId, sector: report.sector, mapping: { [id]: code === "auto" ? "" : code } });
      toast.success(fl("saved", lang)); onReload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <Card className="p-4 overflow-auto" data-testid="ct-mapping">
      <h3 className="font-head font-semibold mb-1">{fl("ctMapping", lang)}</h3>
      <p className="text-[11px] text-muted-foreground mb-2">{fl("ctMapNote", lang)}</p>
      <Table>
        <TableHeader><TableRow><TableHead>{fl("account", lang)}</TableHead><TableHead>L1</TableHead><TableHead className="text-right">{fl("amount", lang)}</TableHead><TableHead>Kode Coretax</TableHead></TableRow></TableHeader>
        <TableBody>
          {report.mapping.map((m) => (
            <TableRow key={`${m.part}-${m.account_id}`} data-testid={`ct-map-${m.account_code}`}>
              <TableCell className="text-xs">{m.account_code} {m.account_name}</TableCell>
              <TableCell><Badge variant="outline" className="text-[10px]">{m.part === "pl" ? "Laba Rugi" : "Neraca"}</Badge></TableCell>
              <TableCell className="num text-right text-xs">{n(m.amount)}</TableCell>
              <TableCell>
                <Select value={m.overridden ? m.code : "auto"} onValueChange={(v) => save(m.account_id, v)} disabled={!canEdit}>
                  <SelectTrigger className="h-8 w-80 text-xs" data-testid={`ct-map-select-${m.account_code}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">{fl("auto", lang)}: {m.auto || "—"}</SelectItem>
                    {(m.part === "pl" ? report.pl_options : report.bs_options).map((o) => <SelectItem key={o.code} value={o.code}>{o.code} · {o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function Check({ ok, label, testid }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${ok ? "text-emerald-700" : "text-rose-600"}`} data-testid={testid}>
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}{label}
    </span>
  );
}

export function CoretaxView({ report, mode, lang, clientId, year, onYear, onSector, onReload, canEdit }) {
  const [adj, setAdj] = useState({});
  const [showMap, setShowMap] = useState(false);
  useEffect(() => {
    setAdj(Object.fromEntries(report.pl.filter((l) => l.kind === "a").map((l) => [l.code, { ...Object.fromEntries(F.map((f) => [f, l[f] || ""])), fcode: l.fcode || "" }])));
  }, [report]);
  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);
  const saveAdj = async () => {
    try {
      const lines = Object.fromEntries(Object.entries(adj).map(([c, v]) => [c, { ...Object.fromEntries(F.map((f) => [f, parseFloat(String(v[f] || 0).replace(",", ".")) || 0])), fcode: v.fcode || "" }]));
      await api.put("/coretax/adjustments", { client_id: clientId, year: report.year, sector: report.sector, lines });
      toast.success(fl("saved", lang)); onReload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const pbtOk = Math.abs(report.profit_before_tax - report.commercial_pbt) < 0.01;
  return (
    <div className="space-y-4" data-testid="coretax-view">
      <Card className="p-3 flex flex-wrap items-center gap-3">
        <Select value={report.sector} onValueChange={onSector}>
          <SelectTrigger className="w-56 h-9" data-testid="ct-sector"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(report.sectors).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => onYear(Number(v))}>
          <SelectTrigger className="w-44 h-9" data-testid="ct-year"><SelectValue /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{fl("taxYear", lang)} {y}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => setShowMap(!showMap)} data-testid="ct-toggle-mapping"><ListTree className="h-4 w-4 mr-1" />{fl("ctMapping", lang)}</Button>
        {canEdit && <Button size="sm" onClick={saveAdj} data-testid="ct-save-adj"><Save className="h-4 w-4 mr-1" />{fl("saveAdj", lang)}</Button>}
        <div className="flex flex-wrap gap-4 ml-auto">
          <Check ok={pbtOk} label={`4800 = ${fl("ctCheckPbt", lang)} (${n(report.commercial_pbt)})`} testid="ct-check-pbt" />
          <Check ok={report.balanced} label={`1700 = 3300 · ${fl(report.balanced ? "balanced" : "unbalanced", lang)}`} testid="ct-check-bs" />
        </div>
      </Card>
      {showMap && <Mapping report={report} clientId={clientId} lang={lang} canEdit={canEdit} onReload={onReload} />}
      <Card className="p-4 overflow-auto" data-testid="ct-pl">
        <h3 className="font-head font-semibold">{fl("ctPl", lang)} <span className="text-xs font-normal text-muted-foreground">· {report.sector_label} · {fl("fiscalIncome", lang)}: <span className="num font-semibold" data-testid="ct-fiscal-income">{n(report.fiscal_income)}</span></span></h3>
        <p className="text-[11px] text-muted-foreground mb-2">{fl("ctNote", lang)}</p>
        <Table>
          <TableHeader><TableRow>{PL_HEAD.map((h, i) => <TableHead key={h} className={`text-[11px] leading-tight ${i >= 2 && i !== 8 ? "text-right" : ""}`}>{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>{report.pl.map((ln, i) => <PLLine key={`${ln.code}-${i}`} ln={ln} adj={adj} setAdj={setAdj} canEdit={canEdit} showAcc={mode !== "summary"} />)}</TableBody>
        </Table>
      </Card>
      <BSCard report={report} mode={mode} lang={lang} />
    </div>
  );
}
